// Main App Component with React Router

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MainLayout } from './layouts/MainLayout';
import { usePermissions } from './hooks/usePermissions';
import { resolveLandingPath } from './config/navigation';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PersonenPage = lazy(() => import('./pages/PersonenPage').then(m => ({ default: m.PersonenPage })));
const KursePage = lazy(() => import('./pages/KursePage').then(m => ({ default: m.KursePage })));
const KursanmeldungPage = lazy(() => import('./pages/KursanmeldungPage').then(m => ({ default: m.KursanmeldungPage })));
const ZahlungenPage = lazy(() => import('./pages/ZahlungenPage').then(m => ({ default: m.ZahlungenPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AdministrationPage = lazy(() => import('./pages/AdministrationPage').then(m => ({ default: m.AdministrationPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then(m => ({ default: m.RolesPage })));
const DataCheckPage = lazy(() => import('./pages/DataCheckPage').then(m => ({ default: m.DataCheckPage })));
const PermissionsPage = lazy(() => import('./pages/PermissionsPage').then(m => ({ default: m.PermissionsPage })));
const PersonDetail = lazy(() => import('./components/Persons/PersonDetail').then(m => ({ default: m.PersonDetail })));
const PersonForm = lazy(() => import('./components/Persons/PersonForm').then(m => ({ default: m.PersonForm })));
const PersonFormExtended = lazy(() => import('./components/Persons/PersonFormExtended').then(m => ({ default: m.PersonFormExtended })));
const FamilyForm = lazy(() => import('./components/Persons/FamilyForm').then(m => ({ default: m.FamilyForm })));
const CourseDetail = lazy(() => import('./components/Courses/CourseDetail').then(m => ({ default: m.CourseDetail })));
const CourseForm = lazy(() => import('./components/Courses/CourseForm').then(m => ({ default: m.CourseForm })));
const DirectDebitDetail = lazy(() => import('./components/Payments/DirectDebitDetail').then(m => ({ default: m.DirectDebitDetail })));
const TextverwaltungPage = lazy(() => import('./pages/TextverwaltungPage').then(m => ({ default: m.TextverwaltungPage })));
const WlsbExport = lazy(() => import('./components/Wlsb/WlsbExport').then(m => ({ default: m.WlsbExport })));
const VenuesManager = lazy(() => import('./components/MasterData/VenuesManager').then(m => ({ default: m.VenuesManager })));
const VenueBookingCalendar = lazy(() => import('./components/MasterData/VenueBookingCalendar').then(m => ({ default: m.VenueBookingCalendar })));
const RegistrationsList = lazy(() => import('./components/Registration/RegistrationsList'));
const RegistrationDetail = lazy(() => import('./components/Registration/RegistrationDetail'));
const IbanValidationReport = lazy(() => import('./components/MasterData/IbanValidationReport').then(m => ({ default: m.IbanValidationReport })));
const DepartmentGroupsPage = lazy(() => import('./pages/DepartmentGroupsPage').then(m => ({ default: m.DepartmentGroupsPage })));
const TwoFactorSetupPage = lazy(() => import('./pages/TwoFactorSetupPage').then(m => ({ default: m.TwoFactorSetupPage })));
const TrustedNetworksList = lazy(() => import('./components/Security/TrustedNetworksList'));
const TrustedDevicesList = lazy(() => import('./components/Security/TrustedDevicesList'));
const WebsiteDashboard = lazy(() => import('./components/Website/WebsiteDashboard'));
const PageEditor = lazy(() => import('./components/Website/PageEditor'));
const SponsorsManager = lazy(() => import('./components/Website/SponsorsManager').then(m => ({ default: m.SponsorsManager })));
const ClubAppPage = lazy(() => import('./pages/ClubAppPage').then(m => ({ default: m.ClubAppPage })));
const CoachTimeAdminPage = lazy(() => import('./pages/CoachTimeAdminPage').then(m => ({ default: m.CoachTimeAdminPage })));
const CompletedCoachTimesPage = lazy(() => import('./pages/CompletedCoachTimesPage').then(m => ({ default: m.CompletedCoachTimesPage })));
const ClubEventCalendar = lazy(() => import('./components/ClubApp/ClubEventCalendar').then(m => ({ default: m.ClubEventCalendar })));
const ClubNewsManager = lazy(() => import('./components/ClubApp/ClubNewsManager').then(m => ({ default: m.ClubNewsManager })));
const InvitationsManager = lazy(() => import('./components/ClubApp/InvitationsManager').then(m => ({ default: m.InvitationsManager })));
const MinorsOverview = lazy(() => import('./components/ClubApp/MinorsOverview').then(m => ({ default: m.MinorsOverview })));
const EmailAccountsManager = lazy(() => import('./components/EmailAccounts/EmailAccountsManager').then(m => ({ default: m.EmailAccountsManager })));
const EmailClient = lazy(() => import('./components/Email/EmailClient').then(m => ({ default: m.EmailClient })));
const ComposeView = lazy(() => import('./components/Email/ComposeView').then(m => ({ default: m.ComposeView })));
const MailRulesView = lazy(() => import('./components/Email/MailRulesView').then(m => ({ default: m.MailRulesView })));
const ActivityView = lazy(() => import('./components/Email/ActivityView').then(m => ({ default: m.ActivityView })));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#5a8c14'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ marginBottom: '16px', fontSize: '48px' }}>⏳</div>
      <div>Lädt...</div>
    </div>
  </div>
);

// Startseite nach dem Login: Hat der Nutzer nur EINEN sichtbaren Menüpunkt,
// wird direkt dessen Seite angesteuert, sonst das Dashboard.
const LandingRedirect = () => {
  const { permissions, loading } = usePermissions();
  if (loading) return <LoadingFallback />;
  return <Navigate to={resolveLandingPath(permissions)} replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <ToastProvider>
            <AuthProvider>
            <Suspense fallback={<LoadingFallback />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/setup-2fa" element={<TwoFactorSetupPage />} />

            {/* Protected Routes with MainLayout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<LandingRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="personen" element={<PersonenPage />} />
              <Route path="personen/neu" element={<PersonFormExtended />} />
              <Route path="personen/familie-anlegen" element={<FamilyForm />} />
              <Route path="personen/:id" element={<PersonDetail />} />
              <Route path="personen/:id/bearbeiten" element={<PersonForm />} />
              <Route path="registrations" element={<RegistrationsList />} />
              <Route path="registrations/:id" element={<RegistrationDetail />} />
              <Route path="kurse" element={<KursePage />} />
              <Route path="kurse/neu" element={<CourseForm />} />
              <Route path="kurse/:id" element={<CourseDetail />} />
              <Route path="kurse/:id/bearbeiten" element={<CourseForm />} />
              <Route path="kursanmeldung" element={<KursanmeldungPage />} />
              <Route path="uebungsleiter-zeiten" element={<CoachTimeAdminPage />} />
              <Route path="abgeschlossene-zeiten" element={<CompletedCoachTimesPage />} />
              <Route path="sportstaetten" element={<VenuesManager />} />
              <Route path="sportstaetten/belegung" element={<VenueBookingCalendar />} />
              <Route path="zahlungen" element={<ZahlungenPage />} />
              <Route path="zahlungen/lastschriften" element={<ZahlungenPage />} />
              <Route path="zahlungen/lastschriften/:id" element={<DirectDebitDetail />} />
              <Route path="teams" element={<DepartmentGroupsPage />} />
              <Route path="club-app" element={<ClubAppPage />} />
              <Route path="club-app/termine" element={<ClubEventCalendar />} />
              <Route path="club-app/beitraege" element={<ClubNewsManager />} />
              <Route path="club-app/einladungen" element={<InvitationsManager />} />
              <Route path="club-app/minderjaehrige" element={<MinorsOverview />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/wlsb" element={<WlsbExport />} />
              <Route path="textverwaltung" element={<TextverwaltungPage />} />
              <Route path="email" element={<EmailClient />} />
              <Route path="email/:accountId" element={<EmailClient />} />
              <Route path="email/:accountId/verfassen" element={<ComposeView />} />
              <Route path="email/:accountId/regeln" element={<MailRulesView />} />
              <Route path="email/:accountId/aktivitaet" element={<ActivityView />} />
              <Route path="email-konten" element={<EmailAccountsManager />} />
              <Route path="administration" element={<AdministrationPage />} />
              <Route path="administration/iban-pruefung" element={<IbanValidationReport />} />
              <Route path="admin/users" element={<UsersPage />} />
              <Route path="admin/roles" element={<RolesPage />} />
              <Route path="admin/permissions" element={<PermissionsPage />} />
              <Route path="admin/datenpruefung" element={<DataCheckPage />} />
              <Route path="admin/security/trusted-networks" element={<TrustedNetworksList />} />
              <Route path="settings/trusted-devices" element={<TrustedDevicesList />} />
              <Route path="website" element={<WebsiteDashboard />} />
              <Route path="website/seiten/neu" element={<PageEditor />} />
              <Route path="website/seiten/:id" element={<PageEditor />} />
              <Route path="website/sponsoren" element={<SponsorsManager />} />
            </Route>

            {/* Catch all - über die Index-Route zur passenden Startseite leiten */}
            <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </AuthProvider>
          </ToastProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
